import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, CheckCircle, AlertTriangle, XCircle, Download, RefreshCw } from "lucide-react";

const CHANNEL_LABELS = {
  efectivo_bbva: "Efectivo BBVA",
  tarjeta_clip: "Tarjeta CLIP",
  rappi: "Rappi",
  uber_eats: "Uber Eats",
  didi: "DiDi",
  oxxo_pay: "Oxxo Pay",
};

const STATUS_CONFIG = {
  ok: { label: "OK", color: "text-green-700 bg-green-50 border-green-200", Icon: CheckCircle },
  alerta: { label: "Alerta", color: "text-yellow-700 bg-yellow-50 border-yellow-200", Icon: AlertTriangle },
  critico: { label: "Critico", color: "text-red-700 bg-red-50 border-red-200", Icon: XCircle },
  // Fix WH22 (2026-08-31): corrio sin un solo movimiento bancario del periodo.
  sin_datos: { label: "Sin extracto", color: "text-slate-600 bg-slate-50 border-slate-200", Icon: AlertTriangle },
};

export default function OpsConciliacionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const res = await api.get(`/ops/reconciliations/${id}`);
      setRecon(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleExport = async () => {
    try {
      const resp = await api.get(`/ops/reconciliations/${id}/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `conciliacion_${id.slice(0, 8)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="p-6 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!recon) return <div className="p-6 text-slate-500">Conciliacion no encontrada.</div>;

  const { status } = recon;
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.alerta;
  const Icon = cfg.Icon;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="conciliacion-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/ops/conciliacion")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              {CHANNEL_LABELS[recon.channel] || recon.channel} — {recon.period_start} al {recon.period_end}
              {recon.channel !== "efectivo_bbva" && (
                <span
                  data-testid="commission-pending-badge"
                  className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200"
                  title="La comisión real de este canal recién se conoce con la liquidación de la plataforma"
                >
                  Comisión pendiente de liquidación
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-500">Conciliacion #{recon.id.slice(0, 8)}</p>
          </div>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
        >
          <Download className="w-4 h-4" /> Exportar Excel
        </button>
      </div>

      {/* Summary cards */}
      <div className={`p-5 rounded-xl border ${cfg.color} flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <Icon className="w-8 h-8" />
          <div>
            <div className="font-bold text-lg">{cfg.label}</div>
            <div className="text-sm opacity-80">Diferencia: ${(recon.difference || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} ({recon.difference_pct || 0}%)</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm opacity-70">Calculado</div>
          <div className="text-sm font-medium">{new Date(recon.computed_at).toLocaleString("es-MX")}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">${(recon.cortes_total || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}</div>
          <div className="text-xs text-slate-500 mt-1">Total declarado en cortes</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">${(recon.deposits_total || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}</div>
          <div className="text-xs text-slate-500 mt-1">Total depositado</div>
        </div>
        <div className={`rounded-xl border p-4 text-center ${recon.difference > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
          <div className={`text-2xl font-bold ${recon.difference > 0 ? "text-red-700" : "text-green-700"}`}>
            ${(recon.difference || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}
          </div>
          <div className="text-xs text-slate-500 mt-1">Diferencia</div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Detalle por corte ({recon.items?.length || 0} cortes)</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {(recon.items || []).map((item, i) => (
            <div key={i} className="p-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-900">{item.corte_date}</div>
                <div className="text-xs text-slate-500 font-mono">{item.corte_id.slice(0, 8)}</div>
                {item.bank_movements?.length > 0 && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    Match: {item.bank_movements.map(m => `${m.id.slice(0, 6)}...`).join(", ")}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-6 text-sm text-right">
                <div>
                  <div className="text-xs text-slate-500">Corte</div>
                  <div className="font-semibold">${(item.corte_amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Depositado</div>
                  <div className="font-semibold">${(item.matched_amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Diferencia</div>
                  <div className={`font-semibold ${item.diff > 50 ? "text-red-600" : item.diff < -50 ? "text-orange-600" : "text-green-600"}`}>
                    {item.diff > 0 ? "+" : ""}{(item.diff || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {(!recon.items || recon.items.length === 0) && (
            <div className="p-8 text-center text-slate-500">No hay items en esta conciliacion.</div>
          )}
        </div>
      </div>
    </div>
  );
}
